import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Grid, Chip,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Checkbox,
  ListItemText, Divider, Alert, CircularProgress, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, Tabs, Tab, Autocomplete, Link,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Save as SaveIcon, Storage as StorageIcon, Folder as FolderIcon,
  Description as DocIcon, Settings as SettingsIcon, Info as InfoIcon,
  AccessTime as TimeIcon, Announcement as MarqueeIcon, Add as AddIcon,
  Edit as EditIcon, Delete as DeleteIcon, Image as ImageIcon,
  Work as WorkIcon,
  Palette as PaletteIcon,
  CalendarMonth as CalendarMonthIcon,
  Language as LanguageIcon,
  Visibility as VisibilityTabIcon,
  Domain as DomainIcon,
  Science as DemoUiTabIcon,
  Security as MfaTabIcon,
  Email as EmailExtractorTabIcon,
  LibraryBooks as LibrariesTabIcon,
} from '@mui/icons-material';
import VisibilitySetup from './VisibilitySetup';
import AdminCompanyDomains from './AdminCompanyDomains';
import EmailExtractorSettingsPanel from './EmailExtractorSettingsPanel';
import ExtractDataLibrariesSettingsPanel from './ExtractDataLibrariesSettingsPanel';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { MFA_METHOD_DEFS, DEFAULT_MFA_METHODS, DEFAULT_REMEMBER_USERID_DAYS, MIN_REMEMBER_USERID_DAYS, MAX_REMEMBER_USERID_DAYS, normalizeRememberUseridDays, MFA_REMEMBER_USERID_DAYS_KEY } from '../../constants/mfaMethods';
import { resolveBrandingLogoImageUrl } from '../../utils/publicAssetUrl';
import { parseBrandingStyle, sxFromBrandingStyle, DEFAULT_PUBLIC_TAGLINE_STYLE, DEFAULT_APP_TAGLINE_STYLE } from '../../utils/brandingStyles';
import {
  MENU_COLOR_TEMPLATE_META,
  MENU_ITEMS_TEMPLATE_META,
  DEFAULT_MENU_TEMPLATE,
  DEFAULT_ITEMS_TEMPLATE,
} from '../../utils/sidebarNavColors';
import { Country } from 'country-state-city';
import useCountries from '../../hooks/useCountries';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { alpha } from '@mui/material/styles';
import { enIN, enUS } from 'date-fns/locale';
import { startOfYear, endOfYear } from 'date-fns';

function phDateToIsoLocal(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatPhHolidayCell(iso, countryCode) {
  if (!iso) return '—';
  const cc = String(countryCode || 'US').toUpperCase();
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y || !m || !d) return String(iso);
  if (cc === 'IN') return `${d}/${m}/${y}`;
  return `${m}/${d}/${y}`;
}

const ORG_DEFAULT_TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)' },
  { value: 'America/Denver', label: 'US Mountain (Denver)' },
  { value: 'America/Chicago', label: 'US Central (Chicago)' },
  { value: 'America/New_York', label: 'US Eastern (New York)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Europe/London', label: 'UK (London)' },
  { value: 'UTC', label: 'UTC' },
];

const PH_COUNTRY_TAB_PALETTE = {
  US: '#1d4ed8',
  IN: '#c2410c',
  CA: '#047857',
  GB: '#0369a1',
  DE: '#57534e',
  FR: '#7c3aed',
  AU: '#0d9488',
  JP: '#be123c',
  CN: '#b91c1c',
  MX: '#15803d',
  BR: '#ca8a04',
};

function phCountryTabColor(code) {
  const c = String(code || '').toUpperCase();
  if (PH_COUNTRY_TAB_PALETTE[c]) return PH_COUNTRY_TAB_PALETTE[c];
  let h = 0;
  for (let i = 0; i < c.length; i += 1) h = (h << 5) - h + c.charCodeAt(i);
  const hue = Math.abs(h) % 360;
  return `hsl(${hue} 48% 38%)`;
}

const AVAILABLE_FILE_TYPES = [
  { value: 'pdf', label: 'PDF (.pdf)' },
  { value: 'txt', label: 'Text (.txt)' },
  { value: 'docx', label: 'Word Document (.docx)' },
  { value: 'doc', label: 'Word Document (.doc)' },
  { value: 'rtf', label: 'Rich Text Format (.rtf)' },
  { value: 'xlsx', label: 'Excel (.xlsx)' },
  { value: 'xls', label: 'Excel (.xls)' },
  { value: 'csv', label: 'CSV (.csv)' },
  { value: 'png', label: 'Image PNG (.png)' },
  { value: 'jpg', label: 'Image JPG (.jpg)' },
  { value: 'jpeg', label: 'Image JPEG (.jpeg)' },
];

const BRANDING_FONT_OPTIONS = [
  { label: 'Georgia (serif)', value: 'Georgia, Cambria, "Times New Roman", serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Calibri', value: 'Calibri, "Segoe UI", Candara, sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", Helvetica, sans-serif' },
  { label: 'Segoe UI / system', value: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
  { label: 'Garamond', value: 'Garamond, "Palatino Linotype", serif' }
];

const BRANDING_COLOR_PRESETS = [
  '#111827',
  '#1e3a8a',
  '#5b21b6',
  '#9d174d',
  '#b91c1c',
  '#047857',
  '#0f766e',
  '#c2410c',
  '#000000'
];

const DEFAULT_COMPANY_NAME_STYLE = {
  color: '',
  fontFamily: 'Georgia, Cambria, "Times New Roman", serif',
  fontWeight: 700,
  fontStyle: 'normal'
};

const DEFAULT_TAGLINE_STYLE = { ...DEFAULT_PUBLIC_TAGLINE_STYLE };
const DEFAULT_TAGLINE_STYLE_APP = { ...DEFAULT_APP_TAGLINE_STYLE };

function brandingStyleToJson(style) {
  const o = {};
  if (style.color?.trim()) o.color = style.color.trim();
  if (style.fontFamily?.trim()) o.fontFamily = style.fontFamily.trim();
  if (style.fontWeight != null && style.fontWeight !== '') o.fontWeight = Number(style.fontWeight);
  if (style.fontStyle === 'italic') o.fontStyle = 'italic';
  if (style.fontStyle === 'normal') o.fontStyle = 'normal';
  if (style.fontSize?.trim()) o.fontSize = style.fontSize.trim();
  if (style.letterSpacing?.trim()) o.letterSpacing = style.letterSpacing.trim();
  return JSON.stringify(o);
}

function brandingStyleFromSetting(raw, defaults) {
  const p = parseBrandingStyle(raw);
  return {
    color: p?.color || defaults.color || '',
    fontFamily: p?.fontFamily || defaults.fontFamily,
    fontWeight: p?.fontWeight ?? defaults.fontWeight,
    fontStyle:
      p?.fontStyle === 'italic'
        ? 'italic'
        : p?.fontStyle === 'normal'
          ? 'normal'
          : defaults.fontStyle,
    fontSize: p?.fontSize || defaults.fontSize || '',
    letterSpacing: p?.letterSpacing || defaults.letterSpacing || ''
  };
}

const SECTION_PALETTES = [
  { border: '#2563eb', bg: 'rgba(37, 99, 235, 0.08)' },
  { border: '#7c3aed', bg: 'rgba(124, 58, 237, 0.07)' },
  { border: '#0d9488', bg: 'rgba(13, 148, 136, 0.08)' },
  { border: '#d97706', bg: 'rgba(217, 119, 6, 0.09)' },
  { border: '#db2777', bg: 'rgba(219, 39, 119, 0.07)' },
  { border: '#15803d', bg: 'rgba(21, 128, 61, 0.08)' },
  { border: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.08)' },
  { border: '#ea580c', bg: 'rgba(234, 88, 12, 0.08)' },
  { border: '#4f46e5', bg: 'rgba(79, 70, 229, 0.07)' },
  { border: '#64748b', bg: 'rgba(100, 116, 139, 0.1)' },
];

const IDLE_DWMQY_KEYS = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];

const DEFAULT_IDLE_DWMQY_POLICY = {
  daily: { enabled: true, idle_minutes: 10, max_times: 2 },
  weekly: { enabled: true, idle_minutes: 10, max_times: 8 },
  monthly: { enabled: true, idle_minutes: 10, max_times: 30 },
  quarterly: { enabled: true, idle_minutes: 10, max_times: 90 },
  yearly: { enabled: true, idle_minutes: 10, max_times: 360 },
};

function normalizeIdleDwmqyPolicy(raw, fallbackMinutes = 10) {
  const base = JSON.parse(JSON.stringify(DEFAULT_IDLE_DWMQY_POLICY));
  const f = Number.isFinite(parseInt(fallbackMinutes, 10)) ? parseInt(fallbackMinutes, 10) : 10;
  IDLE_DWMQY_KEYS.forEach((k) => {
    base[k].idle_minutes = f;
  });
  const src = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  IDLE_DWMQY_KEYS.forEach((k) => {
    const row = src[k] && typeof src[k] === 'object' ? src[k] : {};
    out[k] = {
      enabled: row.enabled !== false,
      idle_minutes: Math.max(1, Math.min(240, parseInt(String(row.idle_minutes || base[k].idle_minutes), 10) || base[k].idle_minutes)),
      max_times: Math.max(0, Math.min(10000, parseInt(String(row.max_times || base[k].max_times), 10) || base[k].max_times)),
    };
  });
  return out;
}

function SettingsSectionBox({ index, title, icon, children, headerActions }) {
  const p = SECTION_PALETTES[(index - 1) % SECTION_PALETTES.length];
  return (
    <Box
      sx={{
        border: '2px solid',
        borderColor: p.border,
        borderRadius: 2,
        bgcolor: p.bg,
        overflow: 'hidden',
        boxShadow: 2,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'rgba(255, 255, 255, 0.92)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box
          component="span"
          sx={{
            minWidth: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: p.border,
            color: 'common.white',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: '1.05rem',
            flexShrink: 0,
            lineHeight: 1,
            boxShadow: 1,
          }}
        >
          {index}
        </Box>
        {icon}
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {headerActions}
      </Box>
      <Box sx={{ p: 2, bgcolor: 'background.paper' }}>{children}</Box>
    </Box>
  );
}

/** Top-level settings categories — one tab each; order matches panels below (sequence 1…n). */
const SETTINGS_MAIN_TABS = [
  { key: 'branding', label: 'Branding' },
  { key: 'companyDomains', label: 'Company domains' },
  { key: 'visibility', label: 'Visibility setup' },
  { key: 'sidebar', label: 'Sidebar navigation' },
  { key: 'documents', label: 'Document upload' },
  { key: 'jobs', label: 'Jobs — limits & assignment' },
  { key: 'deletion', label: 'Record deletion retention' },
  { key: 'idle', label: 'Employee idle (DWMQY)' },
  { key: 'signup', label: 'Signup access' },
  { key: 'mfa', label: 'MFA Settings' },
  { key: 'departments', label: 'Department names' },
  { key: 'teams', label: 'Team names' },
  { key: 'userIdle', label: 'User-specific idle' },
  { key: 'marquee', label: 'Marquee messages' },
  { key: 'system', label: 'System information' },
  { key: 'holidays', label: 'Public holidays' },
  { key: 'orgDefaults', label: 'Default values (organization)' },
  { key: 'demoUi', label: 'Demo / Test Data' },
  { key: 'emailExtractor', label: 'Email Extractor' },
  { key: 'extractLibraries', label: 'Extract Data from Libraries' },
];

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabKey = searchParams.get('tab');
  const initialTabIndex = SETTINGS_MAIN_TABS.findIndex((t) => t.key === initialTabKey);
  const [loading, setLoading] = useState(true);
  const [settingsMainTab, setSettingsMainTab] = useState(
    initialTabKey && initialTabIndex >= 0 ? initialTabIndex : 0
  );
  const [saving, setSaving] = useState(false);
  const [savingEmployee, setSavingEmployee] = useState(false);
  const [savingSignup, setSavingSignup] = useState(false);
  const [savingDeletion, setSavingDeletion] = useState(false);

  useEffect(() => {
    const key = SETTINGS_MAIN_TABS[settingsMainTab]?.key;
    if (!key) return;
    if (searchParams.get('tab') === key) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  }, [settingsMainTab, searchParams, setSearchParams]);

  const [systemInfo, setSystemInfo] = useState(null);
  const [documentSettings, setDocumentSettings] = useState({
    maxSizeMB: 10,
    allowedTypes: ['pdf', 'txt', 'docx', 'doc', 'rtf']
  });
  const [employeeSettings, setEmployeeSettings] = useState({
    idleTimeoutMinutes: 10,
    idleDwmqyPolicy: { ...DEFAULT_IDLE_DWMQY_POLICY },
  });
  const [signupSettings, setSignupSettings] = useState({
    enabled: true,
    providers: {
      google: true,
      linkedin: true,
      microsoft: true
    }
  });
  const [mfaSettings, setMfaSettings] = useState(() => {
    const selected = {};
    MFA_METHOD_DEFS.forEach((m) => {
      selected[m.id] = DEFAULT_MFA_METHODS.includes(m.id);
    });
    return selected;
  });
  const [mfaRememberUseridDays, setMfaRememberUseridDays] = useState(DEFAULT_REMEMBER_USERID_DAYS);
  const [savingMfa, setSavingMfa] = useState(false);
  const [deletionSettings, setDeletionSettings] = useState({
    recordDeletionRetainingDays: 10
  });
  const [jobPipelineSettings, setJobPipelineSettings] = useState({
    maxAttached: 10,
    maxInPipeline: 3,
    maxAssigneesPerJob: 3
  });
  const [savingJobPipeline, setSavingJobPipeline] = useState(false);
  
  // Marquee messages state
  const [marqueeMessages, setMarqueeMessages] = useState([]);
  const [marqueeDialogOpen, setMarqueeDialogOpen] = useState(false);
  const [editingMarquee, setEditingMarquee] = useState(null);
  const [marqueeForm, setMarqueeForm] = useState({
    message: '',
    message_type: 'info',
    priority: 0,
    is_active: true,
    start_date: '',
    end_date: ''
  });
  const [savingMarquee, setSavingMarquee] = useState(false);

  // User idle settings state
  const [usersIdleSettings, setUsersIdleSettings] = useState({ systemDefault: 10, users: [] });
  const [userIdleDialogOpen, setUserIdleDialogOpen] = useState(false);
  const [editingUserIdle, setEditingUserIdle] = useState(null);
  const [userIdleForm, setUserIdleForm] = useState({
    idle_timeout_minutes: '',
    idle_timeout_reason: ''
  });
  const [savingUserIdle, setSavingUserIdle] = useState(false);

  const [brandingLogoPath, setBrandingLogoPath] = useState('');
  const [uploadingBrandingLogo, setUploadingBrandingLogo] = useState(false);
  /** Local object URL for immediate preview before/during upload */
  const [brandingPreviewObjectUrl, setBrandingPreviewObjectUrl] = useState(null);
  const [logoDisplayVersion, setLogoDisplayVersion] = useState(0);
  const [companyProfile, setCompanyProfile] = useState({
    companyName: '',
    companyAddress: '',
    companyCity: '',
    companyZip: '',
    companyWebsite: '',
    companyContactEmail: '',
    companyTagline: '',
    footerCopyright: ''
  });
  const [companyNameStyle, setCompanyNameStyle] = useState(() => ({ ...DEFAULT_COMPANY_NAME_STYLE }));
  const [companyTaglineStyle, setCompanyTaglineStyle] = useState(() => ({ ...DEFAULT_TAGLINE_STYLE }));
  const [companyTaglineStyleApp, setCompanyTaglineStyleApp] = useState(() => ({ ...DEFAULT_TAGLINE_STYLE_APP }));
  const [savingCompanyProfile, setSavingCompanyProfile] = useState(false);

  const [orgDepartments, setOrgDepartments] = useState([]);
  const [orgTeams, setOrgTeams] = useState([]);
  const [savingOrgName, setSavingOrgName] = useState(false);
  const [deptDialog, setDeptDialog] = useState({ open: false, editing: null, name: '', sort_order: 0, is_active: true });
  const [teamDialog, setTeamDialog] = useState({ open: false, editing: null, name: '', sort_order: 0, is_active: true });

  const [orgDefaults, setOrgDefaults] = useState({
    country: 'US',
    timezone: 'America/Los_Angeles',
    currency: 'USD',
  });
  const [savingOrgDefaults, setSavingOrgDefaults] = useState(false);
  const [showDemoDataInUiSetting, setShowDemoDataInUiSetting] = useState(true);
  const [savingDemoUi, setSavingDemoUi] = useState(false);

  const [phYear, setPhYear] = useState(() => new Date().getFullYear());
  const [phCountry, setPhCountry] = useState('US');
  const [phData, setPhData] = useState({
    holidays: [],
    published: false,
    server_time_iso: '',
    server_year: null,
    countries_in_year: [],
    country_code: 'US',
  });
  const [phLoading, setPhLoading] = useState(false);
  const [phBusy, setPhBusy] = useState(false);
  const [phCustomDlg, setPhCustomDlg] = useState({
    open: false,
    name: '',
    officialDate: null,
    observedDate: null,
    observedDirty: false,
  });
  const phYearBootstrappedFromServer = useRef(false);

  const { countries: seededCountries } = useCountries();

  const phCountryOptions = useMemo(() => {
    if (seededCountries?.length) {
      return seededCountries.map((c) => ({
        code: String(c.code || c.isoCode || '').toUpperCase(),
        name: c.label || c.name,
      })).filter((x) => x.code);
    }
    const all = Country.getAllCountries() || [];
    return all
      .map((co) => ({ code: co.isoCode, name: co.name }))
      .filter((x) => x.code)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [seededCountries]);

  const phTabCodes = useMemo(() => {
    const s = new Set((phData.countries_in_year || []).map((c) => String(c.country_code || '').toUpperCase()));
    s.add(String(phCountry || 'US').toUpperCase());
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [phData.countries_in_year, phCountry]);

  const [sidebarAppearance, setSidebarAppearance] = useState({
    menuColorTemplate: DEFAULT_MENU_TEMPLATE,
    menuItemsColorTemplate: DEFAULT_ITEMS_TEMPLATE,
  });
  const [savingSidebarAppearance, setSavingSidebarAppearance] = useState(false);
  const [companyDomainsPrimaryCheck, setCompanyDomainsPrimaryCheck] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/admin/company-domains', { skipErrorToast: true })
      .then((r) => {
        const payload = r.data?.data;
        const list = Array.isArray(payload) ? payload : payload?.domains ?? [];
        if (!cancelled) setCompanyDomainsPrimaryCheck(list);
      })
      .catch(() => {
        if (!cancelled) setCompanyDomainsPrimaryCheck([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (brandingPreviewObjectUrl) {
        URL.revokeObjectURL(brandingPreviewObjectUrl);
      }
    };
  }, [brandingPreviewObjectUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhLoading(true);
      try {
        const r = await api.get('/settings/public-holidays', {
          params: { year: phYear, country: phCountry },
        });
        if (cancelled) return;
        const d = r.data?.data || {};
        if (d.server_year != null && !phYearBootstrappedFromServer.current) {
          const sy = Number(d.server_year);
          if (sy !== phYear) {
            phYearBootstrappedFromServer.current = true;
            setPhYear(sy);
            return;
          }
          phYearBootstrappedFromServer.current = true;
        }
        setPhData({
          holidays: Array.isArray(d.holidays) ? d.holidays : [],
          published: Boolean(d.published),
          server_time_iso: d.server_time_iso || '',
          server_year: d.server_year,
          countries_in_year: Array.isArray(d.countries_in_year) ? d.countries_in_year : [],
          country_code: String(d.country_code || phCountry || 'US').toUpperCase(),
        });
      } catch {
        if (!cancelled) {
          setPhData((p) => ({
            ...p,
            holidays: [],
            countries_in_year: Array.isArray(p.countries_in_year) ? p.countries_in_year : [],
          }));
        }
      } finally {
        if (!cancelled) setPhLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phYear, phCountry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, systemRes, marqueeRes, usersIdleRes, deptRes, teamRes] = await Promise.all([
        api.get('/settings'),
        api.get('/settings/system-info'),
        api.get('/settings/marquee'),
        api.get('/settings/users-idle'),
        api.get('/settings/department-names').catch(() => ({ data: { data: [] } })),
        api.get('/settings/team-names').catch(() => ({ data: { data: [] } })),
      ]);

      // Parse document settings
      const allSettings = settingsRes.data.data;
      if (allSettings.documents) {
        const maxSizeSetting = allSettings.documents.find(s => s.key === 'document.max_size_mb');
        const allowedTypesSetting = allSettings.documents.find(s => s.key === 'document.allowed_types');
        
        setDocumentSettings({
          maxSizeMB: parseInt(maxSizeSetting?.value || '10'),
          allowedTypes: JSON.parse(allowedTypesSetting?.value || '["pdf","txt","docx","doc","rtf"]')
        });
      }

      // Parse employee settings
      if (allSettings.employee) {
        const idleTimeoutSetting = allSettings.employee.find(s => s.key === 'employee.idle_timeout_minutes');
        const idlePolicySetting = allSettings.employee.find((s) => s.key === 'employee.idle_dwmqy_policy');
        let idlePolicyParsed = null;
        try {
          idlePolicyParsed = JSON.parse(idlePolicySetting?.value || '{}');
        } catch {
          idlePolicyParsed = null;
        }
        const idleTimeoutMinutes = parseInt(idleTimeoutSetting?.value || '10');
        setEmployeeSettings({
          idleTimeoutMinutes,
          idleDwmqyPolicy: normalizeIdleDwmqyPolicy(idlePolicyParsed, idleTimeoutMinutes),
        });
      }

      if (allSettings.signup) {
        const enabledSetting = allSettings.signup.find((s) => s.key === 'auth.signup_enabled');
        const providersSetting = allSettings.signup.find((s) => s.key === 'auth.signup_social_providers');
        let providers = [];
        try {
          const parsed = JSON.parse(providersSetting?.value || '[]');
          if (Array.isArray(parsed)) {
            providers = parsed.map((v) => String(v || '').toLowerCase());
          }
        } catch {
          providers = [];
        }
        setSignupSettings({
          enabled: String(enabledSetting?.value || 'true').toLowerCase() !== 'false',
          providers: {
            google: providers.includes('google'),
            linkedin: providers.includes('linkedin'),
            microsoft: providers.includes('microsoft')
          }
        });
      }

      if (allSettings.mfa) {
        const mfaMethodsSetting = allSettings.mfa.find((s) => s.key === 'auth.mfa_methods');
        let methods = [];
        try {
          const parsed = JSON.parse(mfaMethodsSetting?.value || '[]');
          if (Array.isArray(parsed)) {
            methods = parsed.map((v) => String(v || '').toLowerCase());
          }
        } catch {
          methods = [...DEFAULT_MFA_METHODS];
        }
        if (methods.length === 0) methods = [...DEFAULT_MFA_METHODS];
        const selected = {};
        MFA_METHOD_DEFS.forEach((m) => {
          selected[m.id] = methods.includes(m.id);
        });
        setMfaSettings(selected);
        const rememberSetting = allSettings.mfa.find((s) => s.key === MFA_REMEMBER_USERID_DAYS_KEY);
        setMfaRememberUseridDays(normalizeRememberUseridDays(rememberSetting?.value));
      }

      if (allSettings.deletion) {
        const retentionSetting = allSettings.deletion.find(
          (s) => s.key === 'system.record_deletion_retaining_days'
        );
        const parsedRetention = parseInt(retentionSetting?.value || '10', 10);
        setDeletionSettings({
          recordDeletionRetainingDays:
            Number.isFinite(parsedRetention) && parsedRetention >= 0 ? parsedRetention : 10
        });
      }

      if (allSettings.jobs) {
        const att = allSettings.jobs.find((s) => s.key === 'jobs.max_candidates_attached_per_job');
        const pipe = allSettings.jobs.find((s) => s.key === 'jobs.max_candidates_in_pipeline_per_job');
        const maxAssigneesSetting = allSettings.jobs.find((s) => s.key === 'jobs.max_assignees_per_job');
        setJobPipelineSettings({
          maxAttached: Math.max(1, parseInt(att?.value ?? '10', 10) || 10),
          maxInPipeline: Math.max(1, parseInt(pipe?.value ?? '3', 10) || 3),
          maxAssigneesPerJob: Math.max(1, parseInt(maxAssigneesSetting?.value ?? '3', 10) || 3)
        });
      }

      if (allSettings.branding) {
        const logoSetting = allSettings.branding.find((s) => s.key === 'branding.portal_logo_path');
        setBrandingLogoPath((logoSetting?.value || '').trim());
        const pick = (key) => (allSettings.branding.find((s) => s.key === key)?.value || '').trim();
        setCompanyProfile({
          companyName: pick('branding.company_name'),
          companyAddress: pick('branding.company_address'),
          companyCity: pick('branding.company_city'),
          companyZip: pick('branding.company_zip'),
          companyWebsite: pick('branding.company_website'),
          companyContactEmail: pick('branding.company_contact_email'),
          companyTagline: pick('branding.company_tagline'),
          footerCopyright: pick('branding.footer_copyright')
        });
        setCompanyNameStyle(brandingStyleFromSetting(pick('branding.company_name_style'), DEFAULT_COMPANY_NAME_STYLE));
        setCompanyTaglineStyle(brandingStyleFromSetting(pick('branding.company_tagline_style'), DEFAULT_TAGLINE_STYLE));
        setCompanyTaglineStyleApp(
          brandingStyleFromSetting(pick('branding.company_tagline_style_app'), DEFAULT_TAGLINE_STYLE_APP)
        );
      } else {
        setBrandingLogoPath('');
        setCompanyProfile({
          companyName: '',
          companyAddress: '',
          companyCity: '',
          companyZip: '',
          companyWebsite: '',
          companyContactEmail: '',
          companyTagline: '',
          footerCopyright: ''
        });
        setCompanyNameStyle({ ...DEFAULT_COMPANY_NAME_STYLE });
        setCompanyTaglineStyle({ ...DEFAULT_TAGLINE_STYLE });
        setCompanyTaglineStyleApp({ ...DEFAULT_TAGLINE_STYLE_APP });
      }

      if (allSettings.ui) {
        const demoUi = allSettings.ui.find((s) => s.key === 'ui.show_demo_data');
        setShowDemoDataInUiSetting(String(demoUi?.value || 'true').toLowerCase() !== 'false');
        const m = allSettings.ui.find((s) => s.key === 'ui.sidebar_menu_color_template');
        const i = allSettings.ui.find((s) => s.key === 'ui.sidebar_menu_items_color_template');
        setSidebarAppearance({
          menuColorTemplate: (m?.value || '').trim() || DEFAULT_MENU_TEMPLATE,
          menuItemsColorTemplate: (i?.value || '').trim() || DEFAULT_ITEMS_TEMPLATE,
        });
      }

      if (allSettings.organization) {
        const pickOrg = (key) => (allSettings.organization.find((s) => s.key === key)?.value || '').trim();
        const c = pickOrg('org.default_country').toUpperCase().slice(0, 2) || 'US';
        setOrgDefaults({
          country: c,
          timezone: pickOrg('org.default_timezone') || 'America/Los_Angeles',
          currency: (pickOrg('org.default_currency') || 'USD').toUpperCase().slice(0, 3) || 'USD',
        });
      }

      setSystemInfo(systemRes.data.data);
      setMarqueeMessages(marqueeRes.data.data || []);
      setUsersIdleSettings(usersIdleRes.data.data || { systemDefault: 10, users: [] });
      setOrgDepartments(deptRes.data.data || []);
      setOrgTeams(teamRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrgDefaults = async () => {
    setSavingOrgDefaults(true);
    try {
      await api.post('/settings/batch', {
        settings: [
          { key: 'org.default_country', value: String(orgDefaults.country || 'US').toUpperCase().slice(0, 2) },
          { key: 'org.default_timezone', value: String(orgDefaults.timezone || '').trim() || 'America/Los_Angeles' },
          { key: 'org.default_currency', value: String(orgDefaults.currency || 'USD').toUpperCase().slice(0, 3) },
        ],
      });
      toast.success('Default values saved. PTO and other features use these for regional display.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save default values');
    } finally {
      setSavingOrgDefaults(false);
    }
  };

  const handleSaveDemoUiSetting = async () => {
    setSavingDemoUi(true);
    try {
      await api.post('/settings/batch', {
        settings: [{ key: 'ui.show_demo_data', value: showDemoDataInUiSetting ? 'true' : 'false' }],
      });
      toast.success(
        showDemoDataInUiSetting
          ? 'Demo / test UI is visible (sidebar, SEED Demo tab).'
          : 'Demo / test UI is hidden for all users.'
      );
      window.dispatchEvent(new Event('erp4c-sidebar-appearance-updated'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save demo UI setting');
    } finally {
      setSavingDemoUi(false);
    }
  };

  const handleSaveSidebarAppearance = async () => {
    setSavingSidebarAppearance(true);
    try {
      await api.post('/settings/batch', {
        settings: [
          { key: 'ui.sidebar_menu_color_template', value: sidebarAppearance.menuColorTemplate },
          { key: 'ui.sidebar_menu_items_color_template', value: sidebarAppearance.menuItemsColorTemplate },
        ],
      });
      toast.success('Sidebar appearance saved. Open users pick it up on the next navigation or refresh.');
      window.dispatchEvent(new Event('erp4c-sidebar-appearance-updated'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save sidebar appearance');
    } finally {
      setSavingSidebarAppearance(false);
    }
  };

  // Marquee message functions
  const handleOpenMarqueeDialog = (message = null) => {
    if (message) {
      setEditingMarquee(message);
      setMarqueeForm({
        message: message.message,
        message_type: message.message_type,
        priority: message.priority,
        is_active: message.is_active,
        start_date: message.start_date ? message.start_date.split('T')[0] : '',
        end_date: message.end_date ? message.end_date.split('T')[0] : ''
      });
    } else {
      setEditingMarquee(null);
      setMarqueeForm({
        message: '',
        message_type: 'info',
        priority: 0,
        is_active: true,
        start_date: '',
        end_date: ''
      });
    }
    setMarqueeDialogOpen(true);
  };

  const handleSaveMarquee = async () => {
    if (!marqueeForm.message.trim()) {
      toast.error('Message is required');
      return;
    }

    setSavingMarquee(true);
    try {
      const payload = {
        ...marqueeForm,
        start_date: marqueeForm.start_date || null,
        end_date: marqueeForm.end_date || null
      };

      if (editingMarquee) {
        await api.put(`/settings/marquee/${editingMarquee.id}`, payload);
        toast.success('Marquee message updated');
      } else {
        await api.post('/settings/marquee', payload);
        toast.success('Marquee message created');
      }

      setMarqueeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save marquee message');
    } finally {
      setSavingMarquee(false);
    }
  };

  const handleDeleteMarquee = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await api.delete(`/settings/marquee/${id}`);
      toast.success('Marquee message deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete marquee message');
    }
  };

  const handleToggleMarqueeActive = async (message) => {
    try {
      await api.put(`/settings/marquee/${message.id}`, {
        ...message,
        is_active: !message.is_active
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to update marquee message');
    }
  };

  // User idle settings functions
  const handleOpenUserIdleDialog = (user) => {
    setEditingUserIdle(user);
    setUserIdleForm({
      idle_timeout_minutes: user.idleTimeoutMinutes !== null ? user.idleTimeoutMinutes : '',
      idle_timeout_reason: user.idleTimeoutReason || ''
    });
    setUserIdleDialogOpen(true);
  };

  const handleSaveUserIdle = async () => {
    setSavingUserIdle(true);
    try {
      await api.put(`/settings/user-idle/${editingUserIdle.userId}`, {
        idle_timeout_minutes: userIdleForm.idle_timeout_minutes === '' ? null : parseInt(userIdleForm.idle_timeout_minutes),
        idle_timeout_reason: userIdleForm.idle_timeout_reason || null
      });
      toast.success('User idle settings updated');
      setUserIdleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user idle settings');
    } finally {
      setSavingUserIdle(false);
    }
  };

  const handleResetUserIdle = async (user) => {
    if (!window.confirm(`Reset ${user.firstName} ${user.lastName}'s idle timeout to system default?`)) return;
    
    try {
      await api.put(`/settings/user-idle/${user.userId}`, {
        idle_timeout_minutes: null,
        idle_timeout_reason: null
      });
      toast.success('User idle settings reset to default');
      fetchData();
    } catch (error) {
      toast.error('Failed to reset user idle settings');
    }
  };

  const handleSaveDocumentSettings = async () => {
    setSaving(true);
    try {
      await api.post('/settings/batch', {
        settings: [
          { key: 'document.max_size_mb', value: String(documentSettings.maxSizeMB) },
          { key: 'document.allowed_types', value: JSON.stringify(documentSettings.allowedTypes) }
        ]
      });
      toast.success('Document settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveJobPipelineSettings = async () => {
    setSavingJobPipeline(true);
    try {
      await api.post('/settings/batch', {
        settings: [
          {
            key: 'jobs.max_candidates_attached_per_job',
            value: String(Math.max(1, Math.min(500, parseInt(jobPipelineSettings.maxAttached, 10) || 10)))
          },
          {
            key: 'jobs.max_candidates_in_pipeline_per_job',
            value: String(Math.max(1, Math.min(100, parseInt(jobPipelineSettings.maxInPipeline, 10) || 3)))
          },
          {
            key: 'jobs.max_assignees_per_job',
            value: String(Math.max(1, Math.min(50, parseInt(jobPipelineSettings.maxAssigneesPerJob, 10) || 3)))
          }
        ]
      });
      toast.success('Jobs limits saved');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save jobs settings');
    } finally {
      setSavingJobPipeline(false);
    }
  };

  const handleSaveDeletionSettings = async () => {
    setSavingDeletion(true);
    try {
      await api.post('/settings/batch', {
        settings: [
          {
            key: 'system.record_deletion_retaining_days',
            value: String(Math.max(0, parseInt(deletionSettings.recordDeletionRetainingDays, 10) || 0))
          }
        ]
      });
      toast.success('Deletion retention setting saved successfully');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save deletion retention setting');
    } finally {
      setSavingDeletion(false);
    }
  };

  const handleBrandingLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okType =
      /^image\/(jpeg|png)$/i.test(file.type || '') ||
      /\.(jpe?g|png)$/i.test(file.name || '');
    if (!okType) {
      toast.error('Please choose a JPG or PNG image.');
      e.target.value = '';
      return;
    }
    if (brandingPreviewObjectUrl) {
      URL.revokeObjectURL(brandingPreviewObjectUrl);
    }
    const preview = URL.createObjectURL(file);
    setBrandingPreviewObjectUrl(preview);
    setUploadingBrandingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      await api.post('/settings/branding/logo', fd);
      toast.success('Logo saved. It appears on login, public portal, app sidebar, and print/PDF exports.');
      URL.revokeObjectURL(preview);
      setBrandingPreviewObjectUrl(null);
      setLogoDisplayVersion((v) => v + 1);
      window.dispatchEvent(new Event('erp4c-branding-updated'));
      await fetchData();
    } catch (error) {
      const msg = error.response?.data?.message;
      const status = error.response?.status;
      if (status === 413) {
        toast.error('File is too large. Use a JPG or PNG under 10 MB, or compress the image.');
      } else {
        toast.error(msg || 'Failed to upload logo');
      }
      URL.revokeObjectURL(preview);
      setBrandingPreviewObjectUrl(null);
    } finally {
      setUploadingBrandingLogo(false);
      e.target.value = '';
    }
  };

  const handleSaveCompanyProfile = async () => {
    setSavingCompanyProfile(true);
    try {
      await api.post('/settings/batch', {
        settings: [
          { key: 'branding.company_name', value: companyProfile.companyName.trim() },
          { key: 'branding.company_address', value: companyProfile.companyAddress.trim() },
          { key: 'branding.company_city', value: companyProfile.companyCity.trim() },
          { key: 'branding.company_zip', value: companyProfile.companyZip.trim() },
          { key: 'branding.company_website', value: companyProfile.companyWebsite.trim() },
          { key: 'branding.company_contact_email', value: companyProfile.companyContactEmail.trim() },
          { key: 'branding.company_tagline', value: companyProfile.companyTagline.trim() },
          { key: 'branding.footer_copyright', value: companyProfile.footerCopyright.trim() },
          { key: 'branding.company_name_style', value: brandingStyleToJson(companyNameStyle) },
          { key: 'branding.company_tagline_style', value: brandingStyleToJson(companyTaglineStyle) },
          { key: 'branding.company_tagline_style_app', value: brandingStyleToJson(companyTaglineStyleApp) }
        ]
      });
      toast.success('Company details saved. They appear on login, public portal, and the app after login.');
      window.dispatchEvent(new Event('erp4c-branding-updated'));
      await fetchData();
    } catch (error) {
      toast.error('Failed to save company details');
    } finally {
      setSavingCompanyProfile(false);
    }
  };

  const handleRemoveBrandingLogo = async () => {
    if (!window.confirm('Remove the logo from the public portal, login page, and PDF exports?')) return;
    try {
      await api.delete('/settings/branding/logo');
      toast.success('Logo removed');
      setLogoDisplayVersion((v) => v + 1);
      window.dispatchEvent(new Event('erp4c-branding-updated'));
      await fetchData();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const handleSaveEmployeeSettings = async () => {
    setSavingEmployee(true);
    try {
      const normalizedPolicy = normalizeIdleDwmqyPolicy(
        employeeSettings.idleDwmqyPolicy,
        employeeSettings.idleTimeoutMinutes
      );
      const dailyMinutes = normalizedPolicy.daily?.idle_minutes || employeeSettings.idleTimeoutMinutes || 10;
      await api.post('/settings/batch', {
        settings: [
          { key: 'employee.idle_timeout_minutes', value: String(dailyMinutes) },
          { key: 'employee.idle_dwmqy_policy', value: JSON.stringify(normalizedPolicy) },
        ]
      });
      toast.success('Employee settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingEmployee(false);
    }
  };

  const refreshOrgCatalogs = async () => {
    try {
      const [d, t] = await Promise.all([
        api.get('/settings/department-names'),
        api.get('/settings/team-names'),
      ]);
      setOrgDepartments(d.data.data || []);
      setOrgTeams(t.data.data || []);
    } catch {
      /* keep existing */
    }
  };

  const saveDeptDialog = async () => {
    const n = (deptDialog.name || '').trim();
    if (!n) {
      toast.error('Name is required');
      return;
    }
    setSavingOrgName(true);
    try {
      const body = { name: n, sort_order: parseInt(String(deptDialog.sort_order), 10) || 0, is_active: deptDialog.is_active };
      if (deptDialog.editing) {
        await api.put(`/settings/department-names/${deptDialog.editing.id}`, body);
        toast.success('Department name updated');
      } else {
        await api.post('/settings/department-names', body);
        toast.success('Department name added');
      }
      setDeptDialog({ open: false, editing: null, name: '', sort_order: 0, is_active: true });
      await refreshOrgCatalogs();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSavingOrgName(false);
    }
  };

  const saveTeamDialog = async () => {
    const n = (teamDialog.name || '').trim();
    if (!n) {
      toast.error('Name is required');
      return;
    }
    setSavingOrgName(true);
    try {
      const body = { name: n, sort_order: parseInt(String(teamDialog.sort_order), 10) || 0, is_active: teamDialog.is_active };
      if (teamDialog.editing) {
        await api.put(`/settings/team-names/${teamDialog.editing.id}`, body);
        toast.success('Team name updated');
      } else {
        await api.post('/settings/team-names', body);
        toast.success('Team name added');
      }
      setTeamDialog({ open: false, editing: null, name: '', sort_order: 0, is_active: true });
      await refreshOrgCatalogs();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSavingOrgName(false);
    }
  };

  const deleteOrgDepartment = async (row) => {
    if (!window.confirm(`Delete department "${row.name}"?`)) return;
    try {
      await api.delete(`/settings/department-names/${row.id}`);
      toast.success('Deleted');
      await refreshOrgCatalogs();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const deleteOrgTeam = async (row) => {
    if (!window.confirm(`Delete team name "${row.name}"?`)) return;
    try {
      await api.delete(`/settings/team-names/${row.id}`);
      toast.success('Deleted');
      await refreshOrgCatalogs();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const handleSaveSignupSettings = async () => {
    setSavingSignup(true);
    try {
      const providers = ['google', 'linkedin', 'microsoft'].filter((k) => signupSettings.providers[k]);
      await api.post('/settings/batch', {
        settings: [
          { key: 'auth.signup_enabled', value: signupSettings.enabled ? 'true' : 'false' },
          { key: 'auth.signup_social_providers', value: JSON.stringify(providers) }
        ]
      });
      toast.success('Signup settings saved successfully');
    } catch (error) {
      toast.error('Failed to save signup settings');
    } finally {
      setSavingSignup(false);
    }
  };

  const handleSaveMfaSettings = async () => {
    const methods = MFA_METHOD_DEFS.map((m) => m.id).filter((id) => mfaSettings[id]);
    if (methods.length === 0) {
      toast.error('Select at least one MFA method. MFA is mandatory.');
      return;
    }
    const days = normalizeRememberUseridDays(mfaRememberUseridDays);
    setSavingMfa(true);
    try {
      const res = await api.post('/settings/batch', {
        settings: [
          { key: 'auth.mfa_methods', value: JSON.stringify(methods) },
          { key: MFA_REMEMBER_USERID_DAYS_KEY, value: String(days) },
        ],
      });
      const results = res.data?.data || [];
      const failed = results.find((r) => !r.success);
      if (failed) {
        toast.error(failed.reason || 'Failed to save MFA settings');
        return;
      }
      setMfaRememberUseridDays(days);
      toast.success('MFA settings saved. Users will see these options in Profile.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save MFA settings');
    } finally {
      setSavingMfa(false);
    }
  };

  const reloadPublicHolidays = async () => {
    const r = await api.get('/settings/public-holidays', {
      params: { year: phYear, country: phCountry },
    });
    const d = r.data?.data || {};
    setPhData({
      holidays: Array.isArray(d.holidays) ? d.holidays : [],
      published: Boolean(d.published),
      server_time_iso: d.server_time_iso || '',
      server_year: d.server_year,
      countries_in_year: Array.isArray(d.countries_in_year) ? d.countries_in_year : [],
      country_code: String(d.country_code || phCountry || 'US').toUpperCase(),
    });
  };

  const handlePhSeedPreset = async () => {
    setPhBusy(true);
    try {
      const r = await api.post('/settings/public-holidays/seed', { year: phYear, country_code: phCountry });
      const msg = r.data?.message;
      toast.success(msg || 'Preset holidays loaded for review.');
      await reloadPublicHolidays();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load preset holidays.');
    } finally {
      setPhBusy(false);
    }
  };

  const handlePhClearCountry = async () => {
    if (
      !window.confirm(
        `Remove all holidays for ${phYear} / ${phCountry}? You can load presets or add customs again. This cannot be undone.`
      )
    ) {
      return;
    }
    setPhBusy(true);
    try {
      await api.post('/settings/public-holidays/clear', { year: phYear, country_code: phCountry });
      toast.success('Holidays cleared for this country and year.');
      await reloadPublicHolidays();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Clear failed.');
    } finally {
      setPhBusy(false);
    }
  };

  const handlePhPublish = async () => {
    if (!window.confirm(`Publish and lock public holidays for ${phYear} (${phCountry})?`)) return;
    setPhBusy(true);
    try {
      await api.post('/settings/public-holidays/publish', { year: phYear, country_code: phCountry });
      toast.success('Public holidays published.');
      await reloadPublicHolidays();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Publish failed.');
    } finally {
      setPhBusy(false);
    }
  };

  const handlePhUnpublish = async () => {
    if (!window.confirm(`Unpublish ${phYear} (${phCountry}) holidays so you can edit them again?`)) return;
    setPhBusy(true);
    try {
      await api.post('/settings/public-holidays/unpublish', { year: phYear, country_code: phCountry });
      toast.success('Public holidays unpublished.');
      await reloadPublicHolidays();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unpublish failed.');
    } finally {
      setPhBusy(false);
    }
  };

  const handlePhToggleObserved = async (row, checked) => {
    if (phData.published) return;
    try {
      await api.put(`/settings/public-holidays/${row.id}`, { use_observed: checked });
      await reloadPublicHolidays();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Update failed.');
    }
  };

  const handlePhAddCustom = async () => {
    const name = (phCustomDlg.name || '').trim();
    const officialIso = phDateToIsoLocal(phCustomDlg.officialDate);
    const observedIso = phDateToIsoLocal(phCustomDlg.observedDate || phCustomDlg.officialDate);
    if (!name || !officialIso) {
      toast.error('Name and official date are required.');
      return;
    }
    const oy = phCustomDlg.officialDate?.getFullYear();
    if (oy !== phYear) {
      toast.error(`Official date must be in the selected calendar year (${phYear}).`);
      return;
    }
    const oby = (phCustomDlg.observedDate || phCustomDlg.officialDate)?.getFullYear();
    if (oby !== phYear) {
      toast.error(`Observed date must be in the selected calendar year (${phYear}).`);
      return;
    }
    setPhBusy(true);
    try {
      await api.post('/settings/public-holidays/custom', {
        calendar_year: phYear,
        country_code: phCountry,
        name,
        holiday_date: officialIso,
        observed_date: observedIso,
      });
      toast.success('Custom holiday added.');
      setPhCustomDlg({ open: false, name: '', officialDate: null, observedDate: null, observedDirty: false });
      await reloadPublicHolidays();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not add holiday.');
    } finally {
      setPhBusy(false);
    }
  };

  const handlePhDeleteCustom = async (row) => {
    if (!window.confirm(`Remove custom holiday “${row.name}”?`)) return;
    setPhBusy(true);
    try {
      await api.delete(`/settings/public-holidays/${row.id}`);
      toast.success('Removed.');
      await reloadPublicHolidays();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed.');
    } finally {
      setPhBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon sx={{ fontSize: 32 }} />
          System Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure system-wide settings and view system information
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 3,
          p: 1.5,
          borderRadius: 2,
          bgcolor: (theme) => alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.12 : 0.06),
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {SETTINGS_MAIN_TABS.map((tabDef, i) => {
          const p = SECTION_PALETTES[i % SECTION_PALETTES.length];
          const selected = settingsMainTab === i;
          return (
            <Button
              key={tabDef.key}
              size="small"
              onClick={() => setSettingsMainTab(i)}
              disableElevation
              sx={{
                textTransform: 'none',
                fontWeight: selected ? 800 : 600,
                letterSpacing: selected ? 0.02 : undefined,
                borderRadius: 2,
                px: 2,
                py: 1,
                minHeight: 44,
                maxWidth: '100%',
                border: '2px solid',
                borderColor: selected ? p.border : alpha(p.border, 0.5),
                color: selected ? '#ffffff' : 'text.primary',
                bgcolor: selected ? p.border : alpha(p.border, 0.14),
                boxShadow: selected ? `0 0 0 4px ${alpha(p.border, 0.42)}, 0 10px 28px ${alpha(p.border, 0.38)}` : 'none',
                transition: 'background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease',
                '&:hover': {
                  bgcolor: selected ? p.border : alpha(p.border, 0.26),
                  filter: selected ? 'brightness(1.08)' : undefined,
                  borderColor: p.border,
                  boxShadow: selected ? `0 0 0 4px ${alpha(p.border, 0.5)}, 0 12px 32px ${alpha(p.border, 0.42)}` : undefined,
                },
              }}
            >
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, textAlign: 'left' }}>
                <Box
                  component="span"
                  sx={{
                    minWidth: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: selected ? 'rgba(255,255,255,0.28)' : alpha(p.border, 0.35),
                    color: 'inherit',
                    fontSize: '0.75rem',
                    fontWeight: 900,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </Box>
                {tabDef.label}
              </Box>
            </Button>
          );
        })}
      </Box>

      <Grid container spacing={3}>
        {/* Branding — shared with public portal + login */}
        {settingsMainTab === 0 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={1} title="Branding" icon={<ImageIcon color="primary" />}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Upload a <strong>JPG</strong> or <strong>PNG</strong> (max 10&nbsp;MB). It saves as soon as you choose a file. Used on login,
                public portal, and print/PDF views.
              </Alert>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    minWidth: 200,
                    minHeight: 120,
                    maxWidth: 360,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover'
                  }}
                >
                  {brandingPreviewObjectUrl || brandingLogoPath ? (
                    <Box
                      component="img"
                      src={
                        brandingPreviewObjectUrl || resolveBrandingLogoImageUrl(logoDisplayVersion || brandingLogoPath)
                      }
                      alt="Logo preview"
                      onError={() => {
                        console.warn('Branding logo image failed to load (check API /api/public/branding/file).');
                      }}
                      sx={{ maxWidth: '100%', maxHeight: 120, width: 'auto', height: 'auto', objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center">
                      Choose a JPG or PNG to preview here
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                  <Button variant="contained" component="label" disabled={uploadingBrandingLogo} size="small">
                    {uploadingBrandingLogo ? 'Saving…' : 'Choose JPG or PNG'}
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
                      hidden
                      onChange={handleBrandingLogoUpload}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Saves automatically when you select a file
                  </Typography>
                  {brandingLogoPath ? (
                    <Button color="error" variant="outlined" size="small" onClick={handleRemoveBrandingLogo} disabled={uploadingBrandingLogo}>
                      Remove logo
                    </Button>
                  ) : null}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {!companyDomainsPrimaryCheck.some((d) => d.is_primary && d.is_active !== false) ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Primary Domain is missing. Please define Primary domain{' '}
                  <Link component={RouterLink} to="/admin/company-domains" sx={{ fontWeight: 600 }}>
                    here
                  </Link>
                  .
                </Alert>
              ) : null}

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Company details & tagline
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Shown next to the logo on the public portal header and on the login page. Website and email are clickable links.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company name"
                    value={companyProfile.companyName}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, companyName: e.target.value })}
                    placeholder="e.g. Acme Consulting LLC"
                  />
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                    Company name appearance
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel id="branding-name-font-label">Font</InputLabel>
                    <Select
                      labelId="branding-name-font-label"
                      label="Font"
                      value={companyNameStyle.fontFamily || BRANDING_FONT_OPTIONS[0].value}
                      onChange={(e) =>
                        setCompanyNameStyle({ ...companyNameStyle, fontFamily: e.target.value })
                      }
                    >
                      {BRANDING_FONT_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                      {companyNameStyle.fontFamily &&
                      !BRANDING_FONT_OPTIONS.some((o) => o.value === companyNameStyle.fontFamily) ? (
                        <MenuItem value={companyNameStyle.fontFamily}>Current (custom)</MenuItem>
                      ) : null}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Color
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      component="input"
                      type="color"
                      value={companyNameStyle.color || '#111827'}
                      onChange={(e) => setCompanyNameStyle({ ...companyNameStyle, color: e.target.value })}
                      sx={{ width: 44, height: 36, p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', bgcolor: 'background.paper' }}
                    />
                    <TextField
                      size="small"
                      label="Hex"
                      placeholder="#111827"
                      value={companyNameStyle.color}
                      onChange={(e) => setCompanyNameStyle({ ...companyNameStyle, color: e.target.value })}
                      sx={{ width: 120 }}
                    />
                    <Button size="small" onClick={() => setCompanyNameStyle({ ...companyNameStyle, color: '' })}>
                      Default
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    {BRANDING_COLOR_PRESETS.map((hex) => (
                      <Box
                        key={hex}
                        component="button"
                        type="button"
                        title={hex}
                        onClick={() => setCompanyNameStyle({ ...companyNameStyle, color: hex })}
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 0.5,
                          border: '2px solid',
                          borderColor: companyNameStyle.color === hex ? 'primary.main' : 'divider',
                          bgcolor: hex,
                          cursor: 'pointer',
                          p: 0
                        }}
                      />
                    ))}
                  </Box>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel id="branding-name-weight-label">Weight</InputLabel>
                    <Select
                      labelId="branding-name-weight-label"
                      label="Weight"
                      value={companyNameStyle.fontWeight}
                      onChange={(e) =>
                        setCompanyNameStyle({ ...companyNameStyle, fontWeight: Number(e.target.value) })
                      }
                    >
                      {[400, 500, 600, 700].map((w) => (
                        <MenuItem key={w} value={w}>
                          {w}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={companyNameStyle.fontStyle === 'italic'}
                        onChange={(_, c) =>
                          setCompanyNameStyle({
                            ...companyNameStyle,
                            fontStyle: c ? 'italic' : 'normal'
                          })
                        }
                      />
                    }
                    label="Italic"
                    sx={{ mb: 1, display: 'block' }}
                  />
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'action.hover'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Preview (portal header & login)
                    </Typography>
                    <Typography
                      component="div"
                      sx={{
                        ...sxFromBrandingStyle({
                          ...companyNameStyle,
                          color: companyNameStyle.color || undefined
                        }),
                        color: companyNameStyle.color || 'text.primary',
                        fontSize: '1.15rem',
                        lineHeight: 1.3
                      }}
                    >
                      {companyProfile.companyName?.trim() || 'Your company name'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tagline"
                    value={companyProfile.companyTagline}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, companyTagline: e.target.value })}
                    placeholder="Short phrase below your company details"
                  />
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>
                    Tagline — Front page / login appearance
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Use a lighter color (even white) when the front page has a dark banner. This does not affect the logged-in app.
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel id="branding-tag-font-label">Font</InputLabel>
                    <Select
                      labelId="branding-tag-font-label"
                      label="Font"
                      value={companyTaglineStyle.fontFamily || BRANDING_FONT_OPTIONS[0].value}
                      onChange={(e) =>
                        setCompanyTaglineStyle({ ...companyTaglineStyle, fontFamily: e.target.value })
                      }
                    >
                      {BRANDING_FONT_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                      {companyTaglineStyle.fontFamily &&
                      !BRANDING_FONT_OPTIONS.some((o) => o.value === companyTaglineStyle.fontFamily) ? (
                        <MenuItem value={companyTaglineStyle.fontFamily}>Current (custom)</MenuItem>
                      ) : null}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Color (front page)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      component="input"
                      type="color"
                      value={companyTaglineStyle.color || '#1e3a8a'}
                      onChange={(e) => setCompanyTaglineStyle({ ...companyTaglineStyle, color: e.target.value })}
                      sx={{ width: 44, height: 36, p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', bgcolor: 'background.paper' }}
                    />
                    <TextField
                      size="small"
                      label="Hex"
                      placeholder="#1e3a8a"
                      value={companyTaglineStyle.color}
                      onChange={(e) => setCompanyTaglineStyle({ ...companyTaglineStyle, color: e.target.value })}
                      sx={{ width: 120 }}
                    />
                    <Button size="small" onClick={() => setCompanyTaglineStyle({ ...companyTaglineStyle, color: '#ffffff' })}>
                      White
                    </Button>
                    <Button size="small" onClick={() => setCompanyTaglineStyle({ ...DEFAULT_TAGLINE_STYLE })}>
                      Reset
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    {['#ffffff', '#f8fafc', '#1e3a8a', '#0f172a', '#6366f1', '#0ea5e9', ...BRANDING_COLOR_PRESETS].filter((v, i, a) => a.indexOf(v) === i).map((hex) => (
                      <Box
                        key={`tag-pub-${hex}`}
                        component="button"
                        type="button"
                        title={hex}
                        onClick={() => setCompanyTaglineStyle({ ...companyTaglineStyle, color: hex })}
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 0.5,
                          border: '2px solid',
                          borderColor: companyTaglineStyle.color === hex ? 'primary.main' : 'divider',
                          bgcolor: hex,
                          cursor: 'pointer',
                          p: 0
                        }}
                      />
                    ))}
                  </Box>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel id="branding-tag-weight-label">Weight</InputLabel>
                    <Select
                      labelId="branding-tag-weight-label"
                      label="Weight"
                      value={companyTaglineStyle.fontWeight}
                      onChange={(e) =>
                        setCompanyTaglineStyle({ ...companyTaglineStyle, fontWeight: Number(e.target.value) })
                      }
                    >
                      {[400, 500, 600, 700].map((w) => (
                        <MenuItem key={w} value={w}>
                          {w}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    fullWidth
                    label="Font size (e.g. 1.2rem)"
                    value={companyTaglineStyle.fontSize || ''}
                    onChange={(e) => setCompanyTaglineStyle({ ...companyTaglineStyle, fontSize: e.target.value })}
                    sx={{ mb: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={companyTaglineStyle.fontStyle === 'italic'}
                        onChange={(_, c) =>
                          setCompanyTaglineStyle({
                            ...companyTaglineStyle,
                            fontStyle: c ? 'italic' : 'normal'
                          })
                        }
                      />
                    }
                    label="Italic"
                    sx={{ mb: 1, display: 'block' }}
                  />
                  <Box
                    sx={{
                      mt: 0.5,
                      mb: 2,
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: companyTaglineStyle.color === '#ffffff' || companyTaglineStyle.color === '#fff' ? '#0f172a' : 'action.hover'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Preview — front page / login
                    </Typography>
                    <Typography
                      component="div"
                      sx={{
                        ...sxFromBrandingStyle({
                          ...companyTaglineStyle,
                          color: companyTaglineStyle.color || undefined
                        }),
                        color: companyTaglineStyle.color || '#1e3a8a',
                        fontSize: companyTaglineStyle.fontSize || '1.12rem',
                        lineHeight: 1.45,
                        fontStyle: companyTaglineStyle.fontStyle === 'italic' ? 'italic' : 'normal',
                        fontWeight: companyTaglineStyle.fontWeight || 600
                      }}
                    >
                      {companyProfile.companyTagline?.trim() || 'Your tagline'}
                    </Typography>
                  </Box>

                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>
                    Tagline — Inside the software (sidebar)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Use a dark/clear color for the light sidebar after login (white text disappears on white).
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel id="branding-tag-app-font-label">Font</InputLabel>
                    <Select
                      labelId="branding-tag-app-font-label"
                      label="Font"
                      value={companyTaglineStyleApp.fontFamily || BRANDING_FONT_OPTIONS[0].value}
                      onChange={(e) =>
                        setCompanyTaglineStyleApp({ ...companyTaglineStyleApp, fontFamily: e.target.value })
                      }
                    >
                      {BRANDING_FONT_OPTIONS.map((o) => (
                        <MenuItem key={`app-${o.value}`} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Color (in-app)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      component="input"
                      type="color"
                      value={companyTaglineStyleApp.color || '#0f172a'}
                      onChange={(e) => setCompanyTaglineStyleApp({ ...companyTaglineStyleApp, color: e.target.value })}
                      sx={{ width: 44, height: 36, p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', bgcolor: 'background.paper' }}
                    />
                    <TextField
                      size="small"
                      label="Hex"
                      placeholder="#0f172a"
                      value={companyTaglineStyleApp.color}
                      onChange={(e) => setCompanyTaglineStyleApp({ ...companyTaglineStyleApp, color: e.target.value })}
                      sx={{ width: 120 }}
                    />
                    <Button size="small" onClick={() => setCompanyTaglineStyleApp({ ...DEFAULT_TAGLINE_STYLE_APP })}>
                      Reset
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    {['#0f172a', '#1e3a8a', '#0c4a6e', '#334155', '#6366f1', ...BRANDING_COLOR_PRESETS].filter((v, i, a) => a.indexOf(v) === i).map((hex) => (
                      <Box
                        key={`tag-app-${hex}`}
                        component="button"
                        type="button"
                        title={hex}
                        onClick={() => setCompanyTaglineStyleApp({ ...companyTaglineStyleApp, color: hex })}
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 0.5,
                          border: '2px solid',
                          borderColor: companyTaglineStyleApp.color === hex ? 'primary.main' : 'divider',
                          bgcolor: hex,
                          cursor: 'pointer',
                          p: 0
                        }}
                      />
                    ))}
                  </Box>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel id="branding-tag-app-weight-label">Weight</InputLabel>
                    <Select
                      labelId="branding-tag-app-weight-label"
                      label="Weight"
                      value={companyTaglineStyleApp.fontWeight}
                      onChange={(e) =>
                        setCompanyTaglineStyleApp({ ...companyTaglineStyleApp, fontWeight: Number(e.target.value) })
                      }
                    >
                      {[400, 500, 600, 700].map((w) => (
                        <MenuItem key={`app-w-${w}`} value={w}>
                          {w}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    fullWidth
                    label="Font size (e.g. 0.95rem)"
                    value={companyTaglineStyleApp.fontSize || ''}
                    onChange={(e) => setCompanyTaglineStyleApp({ ...companyTaglineStyleApp, fontSize: e.target.value })}
                    sx={{ mb: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={companyTaglineStyleApp.fontStyle === 'italic'}
                        onChange={(_, c) =>
                          setCompanyTaglineStyleApp({
                            ...companyTaglineStyleApp,
                            fontStyle: c ? 'italic' : 'normal'
                          })
                        }
                      />
                    }
                    label="Italic"
                    sx={{ mb: 1, display: 'block' }}
                  />
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: '#ffffff'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Preview — inside software (on white)
                    </Typography>
                    <Typography
                      component="div"
                      sx={{
                        ...sxFromBrandingStyle({
                          ...companyTaglineStyleApp,
                          color: companyTaglineStyleApp.color || undefined
                        }),
                        color: companyTaglineStyleApp.color || '#0f172a',
                        fontSize: companyTaglineStyleApp.fontSize || '0.95rem',
                        lineHeight: 1.4,
                        fontStyle: companyTaglineStyleApp.fontStyle === 'italic' ? 'italic' : 'normal',
                        fontWeight: companyTaglineStyleApp.fontWeight || 600
                      }}
                    >
                      {companyProfile.companyTagline?.trim() || 'Your tagline'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street address"
                    value={companyProfile.companyAddress}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, companyAddress: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={companyProfile.companyCity}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, companyCity: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="ZIP / Postal code"
                    value={companyProfile.companyZip}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, companyZip: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={companyProfile.companyWebsite}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, companyWebsite: e.target.value })}
                    placeholder="https://example.com"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Contact email"
                    value={companyProfile.companyContactEmail}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, companyContactEmail: e.target.value })}
                    placeholder="hello@example.com"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Footer / copyright line"
                    value={companyProfile.footerCopyright}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, footerCopyright: e.target.value })}
                    placeholder={`© ${new Date().getFullYear()} Your Company. All rights reserved.`}
                    helperText="Shown on login, signup, and the app sidebar. Leave blank to use © year + company name."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveCompanyProfile}
                    disabled={savingCompanyProfile}
                  >
                    {savingCompanyProfile ? 'Saving…' : 'Save company details'}
                  </Button>
                </Grid>
              </Grid>
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 1 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={2} title="Company domains" icon={<DomainIcon color="primary" />}>
            <AdminCompanyDomains embedded />
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 2 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={3} title="Visibility setup" icon={<VisibilityTabIcon color="primary" />}>
            <VisibilitySetup embedded />
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 3 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={4} title="Sidebar navigation (main app)" icon={<PaletteIcon color="primary" />}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Choose a <strong>dark menu</strong> palette for section headers (General, My Items, …) and a separate style for{' '}
              <strong>nested menu items</strong>. Changes apply for all signed-in users after they navigate or refresh.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="sidebar-menu-template">Menu — section headers</InputLabel>
                  <Select
                    labelId="sidebar-menu-template"
                    label="Menu — section headers"
                    value={sidebarAppearance.menuColorTemplate}
                    onChange={(e) =>
                      setSidebarAppearance((p) => ({ ...p, menuColorTemplate: e.target.value }))
                    }
                  >
                    {MENU_COLOR_TEMPLATE_META.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="sidebar-items-template">Menu items — nested rows</InputLabel>
                  <Select
                    labelId="sidebar-items-template"
                    label="Menu items — nested rows"
                    value={sidebarAppearance.menuItemsColorTemplate}
                    onChange={(e) =>
                      setSidebarAppearance((p) => ({ ...p, menuItemsColorTemplate: e.target.value }))
                    }
                  >
                    {MENU_ITEMS_TEMPLATE_META.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={savingSidebarAppearance ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveSidebarAppearance}
                  disabled={savingSidebarAppearance}
                >
                  {savingSidebarAppearance ? 'Saving…' : 'Save sidebar appearance'}
                </Button>
              </Grid>
            </Grid>
          </SettingsSectionBox>
        </Grid>
        )}

        {/* Document Upload Settings */}
        {settingsMainTab === 4 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={5} title="Document upload settings" icon={<DocIcon color="primary" />}>
              <Alert severity="info" sx={{ mb: 3 }}>
                These settings control what files users can upload to the system.
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Maximum File Size"
                    type="number"
                    value={documentSettings.maxSizeMB}
                    onChange={(e) => setDocumentSettings({
                      ...documentSettings,
                      maxSizeMB: Math.max(1, Math.min(100, parseInt(e.target.value) || 1))
                    })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">MB</InputAdornment>,
                    }}
                    helperText="Maximum file size allowed (1-100 MB)"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Allowed File Types</InputLabel>
                    <Select
                      multiple
                      value={documentSettings.allowedTypes}
                      onChange={(e) => setDocumentSettings({
                        ...documentSettings,
                        allowedTypes: e.target.value
                      })}
                      input={<OutlinedInput label="Allowed File Types" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={`.${value.toUpperCase()}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {AVAILABLE_FILE_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Checkbox checked={documentSettings.allowedTypes.includes(type.value)} />
                          <ListItemText primary={type.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Currently allowed types:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {documentSettings.allowedTypes.map((type) => (
                      <Chip
                        key={type}
                        label={`.${type}`}
                        size="small"
                        sx={{ bgcolor: 'primary.main', color: 'white' }}
                      />
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSaveDocumentSettings}
                    disabled={saving || documentSettings.allowedTypes.length === 0}
                    fullWidth
                    sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
                  >
                    {saving ? 'Saving...' : 'Save Document Settings'}
                  </Button>
                </Grid>
              </Grid>
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 5 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={6} title="Jobs — limits & assignment" icon={<WorkIcon color="primary" />}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Limit candidate volume per job, pipeline concurrency, and how many employee assignees can own a job at
              once (used by “Assign to me” on the jobs list). Adjust if your process allows more parallel interviews or
              more owners per role.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max candidates attached per job"
                  value={jobPipelineSettings.maxAttached}
                  onChange={(e) =>
                    setJobPipelineSettings((p) => ({
                      ...p,
                      maxAttached: Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 1))
                    }))
                  }
                  helperText="Maximum submission rows per job (all statuses)."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max candidates in pipeline per job"
                  value={jobPipelineSettings.maxInPipeline}
                  onChange={(e) =>
                    setJobPipelineSettings((p) => ({
                      ...p,
                      maxInPipeline: Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1))
                    }))
                  }
                  helperText="Active funnel only (e.g. submitted … offer made). Terminal statuses free a pipeline slot."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max assignees per job"
                  value={jobPipelineSettings.maxAssigneesPerJob}
                  onChange={(e) =>
                    setJobPipelineSettings((p) => ({
                      ...p,
                      maxAssigneesPerJob: Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1))
                    }))
                  }
                  helperText="Maximum employee owners per job; “Assign to me” is disabled when the job reaches this count."
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={savingJobPipeline ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveJobPipelineSettings}
                  disabled={savingJobPipeline}
                  fullWidth
                  sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
                >
                  {savingJobPipeline ? 'Saving…' : 'Save jobs limits'}
                </Button>
              </Grid>
            </Grid>
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 6 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={7} title="Record deletion retention" icon={<DeleteIcon color="primary" />}>
            <Alert severity="info" sx={{ mb: 2 }}>
              When users choose <strong>Delete permanently</strong>, the record is first marked for deletion and removed
              after the configured number of days. Set it to <strong>0</strong> for immediate deletion.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Record Deletion Retaining Days"
                  value={deletionSettings.recordDeletionRetainingDays}
                  onChange={(e) =>
                    setDeletionSettings((prev) => ({
                      ...prev,
                      recordDeletionRetainingDays: Math.max(0, Math.min(3650, parseInt(e.target.value, 10) || 0))
                    }))
                  }
                  helperText="Example: 10 means keep for 10 days, then purge. 0 means delete immediately."
                  InputProps={{
                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={savingDeletion ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveDeletionSettings}
                  disabled={savingDeletion}
                  fullWidth
                >
                  {savingDeletion ? 'Saving...' : 'Save Deletion Retention'}
                </Button>
              </Grid>
            </Grid>
          </SettingsSectionBox>
        </Grid>
        )}

        {/* Employee Settings */}
        {settingsMainTab === 7 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={8} title="Employee Idle Time Allowed in the System" icon={<TimeIcon color="primary" />}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Configure the DWMQY policy: <strong>Daily, Weekly, Monthly, Quarterly, Yearly</strong>. For each period,
                set the idle minutes threshold and how many times it is allowed. Enabled limits are shown to users on the
                dashboard as <strong>no impact</strong> when within limit; overages are highlighted for review.
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>DWMQY Format</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Idle minutes</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">How many times</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="center">Enable / disable</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {IDLE_DWMQY_KEYS.map((k) => {
                          const row = employeeSettings.idleDwmqyPolicy?.[k] || DEFAULT_IDLE_DWMQY_POLICY[k];
                          const label =
                            k === 'daily'
                              ? 'Daily'
                              : k === 'weekly'
                                ? 'Weekly'
                                : k === 'monthly'
                                  ? 'Monthly'
                                  : k === 'quarterly'
                                    ? 'Quarterly'
                                    : 'Yearly';
                          return (
                            <TableRow key={k}>
                              <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                              <TableCell align="right" sx={{ width: 160 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={row.idle_minutes}
                                  onChange={(e) =>
                                    setEmployeeSettings((prev) => ({
                                      ...prev,
                                      idleTimeoutMinutes:
                                        k === 'daily'
                                          ? Math.max(1, Math.min(240, parseInt(e.target.value, 10) || 1))
                                          : prev.idleTimeoutMinutes,
                                      idleDwmqyPolicy: {
                                        ...prev.idleDwmqyPolicy,
                                        [k]: {
                                          ...(prev.idleDwmqyPolicy?.[k] || {}),
                                          idle_minutes: Math.max(1, Math.min(240, parseInt(e.target.value, 10) || 1)),
                                        },
                                      },
                                    }))
                                  }
                                  inputProps={{ min: 1, max: 240 }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ width: 180 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={row.max_times}
                                  onChange={(e) =>
                                    setEmployeeSettings((prev) => ({
                                      ...prev,
                                      idleDwmqyPolicy: {
                                        ...prev.idleDwmqyPolicy,
                                        [k]: {
                                          ...(prev.idleDwmqyPolicy?.[k] || {}),
                                          max_times: Math.max(0, Math.min(10000, parseInt(e.target.value, 10) || 0)),
                                        },
                                      },
                                    }))
                                  }
                                  inputProps={{ min: 0, max: 10000 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Switch
                                  checked={row.enabled !== false}
                                  onChange={(e) =>
                                    setEmployeeSettings((prev) => ({
                                      ...prev,
                                      idleDwmqyPolicy: {
                                        ...prev.idleDwmqyPolicy,
                                        [k]: {
                                          ...(prev.idleDwmqyPolicy?.[k] || {}),
                                          enabled: e.target.checked,
                                        },
                                      },
                                    }))
                                  }
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ fontSize: '0.85rem' }}>
                    <strong>How it works:</strong><br />
                    • When a user logs in, they are automatically "punched in"<br />
                    • Daily row minutes ({employeeSettings.idleDwmqyPolicy?.daily?.idle_minutes || employeeSettings.idleTimeoutMinutes} min) is used for idle detection<br />
                    • System counts idle occurrences by D/W/M/Q/Y per enabled rows<br />
                    • Within allowed counts = no impact message; beyond limit = shown as over limit on dashboard<br />
                    • Per-user occurrences are stored for reporting
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={savingEmployee ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSaveEmployeeSettings}
                    disabled={savingEmployee}
                    fullWidth
                    sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                  >
                    {savingEmployee ? 'Saving...' : 'Save Employee Settings'}
                  </Button>
                </Grid>
              </Grid>
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 8 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={9} title="Signup access" icon={<SettingsIcon color="primary" />}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Control whether the public signup page is available and which social signup buttons are visible.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={signupSettings.enabled}
                        onChange={(e) => setSignupSettings((s) => ({ ...s, enabled: e.target.checked }))}
                      />
                    }
                    label="Enable public signup page"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Social signup buttons to show
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={signupSettings.providers.google}
                        onChange={(e) =>
                          setSignupSettings((s) => ({
                            ...s,
                            providers: { ...s.providers, google: e.target.checked }
                          }))
                        }
                      />
                    }
                    label="Google"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={signupSettings.providers.linkedin}
                        onChange={(e) =>
                          setSignupSettings((s) => ({
                            ...s,
                            providers: { ...s.providers, linkedin: e.target.checked }
                          }))
                        }
                      />
                    }
                    label="LinkedIn"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={signupSettings.providers.microsoft}
                        onChange={(e) =>
                          setSignupSettings((s) => ({
                            ...s,
                            providers: { ...s.providers, microsoft: e.target.checked }
                          }))
                        }
                      />
                    }
                    label="Windows / Microsoft"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={savingSignup ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSaveSignupSettings}
                    disabled={savingSignup}
                    fullWidth
                  >
                    {savingSignup ? 'Saving...' : 'Save Signup Settings'}
                  </Button>
                </Grid>
              </Grid>
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 9 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={10} title="MFA Settings" icon={<MfaTabIcon color="primary" />}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Multi-factor authentication is mandatory. Select at least one method. Users will only see the
                methods you enable here under <strong>Profile → Security</strong> to set up MFA.
              </Alert>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Authenticator apps
              </Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {MFA_METHOD_DEFS.filter((m) => m.category === 'authenticator').map((m) => (
                  <Grid item xs={12} sm={6} key={m.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(mfaSettings[m.id])}
                          onChange={(e) =>
                            setMfaSettings((s) => ({ ...s, [m.id]: e.target.checked }))
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{m.description}</Typography>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                OTP / phone delivery
              </Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {MFA_METHOD_DEFS.filter((m) => m.category !== 'authenticator').map((m) => (
                  <Grid item xs={12} sm={6} key={m.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(mfaSettings[m.id])}
                          onChange={(e) =>
                            setMfaSettings((s) => ({ ...s, [m.id]: e.target.checked }))
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{m.description}</Typography>
                        </Box>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selected: {MFA_METHOD_DEFS.filter((m) => mfaSettings[m.id]).map((m) => m.label).join(', ') || 'none'}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Remember userid on login
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Controls how long the login page may remember the userid/email when a user checks
                &quot;Remember my userid&quot;. This does not skip MFA.
              </Alert>
              <TextField
                type="number"
                label="Remember userid (days)"
                value={mfaRememberUseridDays}
                onChange={(e) => setMfaRememberUseridDays(e.target.value)}
                inputProps={{ min: MIN_REMEMBER_USERID_DAYS, max: MAX_REMEMBER_USERID_DAYS, step: 1 }}
                helperText={`Allowed range: ${MIN_REMEMBER_USERID_DAYS}–${MAX_REMEMBER_USERID_DAYS} days (default ${DEFAULT_REMEMBER_USERID_DAYS})`}
                sx={{ mb: 2, maxWidth: 320 }}
              />
              <Button
                variant="contained"
                startIcon={savingMfa ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveMfaSettings}
                disabled={savingMfa || MFA_METHOD_DEFS.every((m) => !mfaSettings[m.id])}
                fullWidth
              >
                {savingMfa ? 'Saving...' : 'Save MFA Settings'}
              </Button>
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 10 && (
        <Grid item xs={12}>
          <SettingsSectionBox
            index={10}
            title="Department names"
            icon={<FolderIcon color="primary" />}
            headerActions={
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() =>
                  setDeptDialog({ open: true, editing: null, name: '', sort_order: 0, is_active: true })
                }
              >
                Add
              </Button>
            }
          >
            <Alert severity="info" sx={{ mb: 2 }} icon={false}>
              <Typography variant="body2">
                Use this list as the canonical set of <strong>department</strong> labels (e.g. for employee HR
                profiles and forms). Inactive items stay in the database but can be hidden from future pickers when you
                wire the app to filter by <code>is_active</code>.
              </Typography>
            </Alert>
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell width={90}>Order</TableCell>
                    <TableCell width={90}>Active</TableCell>
                    <TableCell align="right" width={120}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgDepartments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          No department names yet. Click Add to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orgDepartments.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.sort_order}</TableCell>
                        <TableCell>
                          {r.is_active ? (
                            <Chip label="Yes" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="No" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setDeptDialog({
                                open: true,
                                editing: r,
                                name: r.name,
                                sort_order: r.sort_order,
                                is_active: r.is_active,
                              })
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => deleteOrgDepartment(r)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 11 && (
        <Grid item xs={12}>
          <SettingsSectionBox
            index={11}
            title="Team names"
            icon={<WorkIcon color="primary" />}
            headerActions={
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => setTeamDialog({ open: true, editing: null, name: '', sort_order: 0, is_active: true })}
              >
                Add
              </Button>
            }
          >
            <Alert severity="info" sx={{ mb: 2 }} icon={false}>
              <Typography variant="body2">
                Separate from departments: <strong>team</strong> labels (squad, practice line, sub-unit). Same add /
                edit / delete pattern as department names.
              </Typography>
            </Alert>
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell width={90}>Order</TableCell>
                    <TableCell width={90}>Active</TableCell>
                    <TableCell align="right" width={120}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgTeams.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          No team names yet. Click Add to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orgTeams.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.sort_order}</TableCell>
                        <TableCell>
                          {r.is_active ? (
                            <Chip label="Yes" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="No" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setTeamDialog({
                                open: true,
                                editing: r,
                                name: r.name,
                                sort_order: r.sort_order,
                                is_active: r.is_active,
                              })
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => deleteOrgTeam(r)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SettingsSectionBox>
        </Grid>
        )}

        {/* User-Specific Idle Settings */}
        {settingsMainTab === 12 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={12} title="User-specific idle settings" icon={<TimeIcon color="primary" />}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Configure individual idle timeout for specific users. Users without custom settings will use the system default ({usersIdleSettings.systemDefault} minutes).
              </Alert>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Idle Timeout</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usersIdleSettings.users?.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.firstName} {user.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {user.isCustom ? (
                            <Chip 
                              label={`${user.idleTimeoutMinutes} min`} 
                              size="small" 
                              color="primary"
                            />
                          ) : (
                            <Chip 
                              label={`${usersIdleSettings.systemDefault} min (default)`} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, fontSize: '0.75rem' }}>
                            {user.idleTimeoutReason || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenUserIdleDialog(user)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {user.isCustom && (
                            <IconButton size="small" color="warning" onClick={() => handleResetUserIdle(user)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
          </SettingsSectionBox>
        </Grid>
        )}

        {/* Marquee Messages */}
        {settingsMainTab === 13 && (
        <Grid item xs={12}>
          <SettingsSectionBox
            index={13}
            title="Marquee messages"
            icon={<MarqueeIcon color="primary" />}
            headerActions={
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenMarqueeDialog()}
                size="small"
              >
                Add message
              </Button>
            }
          >

              <Alert severity="info" sx={{ mb: 3 }}>
                Marquee messages appear as a scrolling banner at the top of the application. 
                Use them to communicate important announcements to all users.
              </Alert>

              {marqueeMessages.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Message</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell>Date Range</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {marqueeMessages.map((msg) => (
                        <TableRow key={msg.id}>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography variant="body2" noWrap>
                              {msg.message}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={msg.message_type}
                              size="small"
                              color={
                                msg.message_type === 'success' ? 'success' :
                                msg.message_type === 'warning' ? 'warning' :
                                msg.message_type === 'error' ? 'error' : 'info'
                              }
                            />
                          </TableCell>
                          <TableCell>{msg.priority}</TableCell>
                          <TableCell>
                            <Switch
                              checked={msg.is_active}
                              onChange={() => handleToggleMarqueeActive(msg)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {msg.start_date || msg.end_date ? (
                              <Typography variant="caption">
                                {msg.start_date ? new Date(msg.start_date).toLocaleDateString() : 'Always'} - 
                                {msg.end_date ? new Date(msg.end_date).toLocaleDateString() : 'Always'}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">Always</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleOpenMarqueeDialog(msg)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteMarquee(msg.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    No marquee messages. Click "Add Message" to create one.
                  </Typography>
                </Box>
              )}
          </SettingsSectionBox>
        </Grid>
        )}

        {/* System Information */}
        {settingsMainTab === 14 && (
        <Grid item xs={12}>
          <SettingsSectionBox index={14} title="System information" icon={<InfoIcon color="primary" />}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                This information is read-only and cannot be changed from here.
              </Alert>

              {systemInfo && (
                <>
                  {/* Database Info */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <StorageIcon sx={{ color: 'text.secondary' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Database
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <InfoRow label="Type" value={systemInfo.database?.type} />
                      <InfoRow label="Host" value={systemInfo.database?.host} />
                      <InfoRow label="Port" value={systemInfo.database?.port} />
                      <InfoRow label="Database Name" value={systemInfo.database?.name} />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Storage Info */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <FolderIcon sx={{ color: 'text.secondary' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        File Storage
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <InfoRow label="Upload Path" value={systemInfo.storage?.uploadPath} code />
                      <InfoRow label="Documents Path" value={systemInfo.storage?.documentsPath} code />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Server Info */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <SettingsIcon sx={{ color: 'text.secondary' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Server
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <InfoRow label="Application" value={systemInfo.application?.name} />
                      <InfoRow label="Version" value={systemInfo.application?.version} />
                      <InfoRow label="Node.js" value={systemInfo.server?.nodeVersion} />
                      <InfoRow label="Environment" value={systemInfo.server?.environment} />
                      <InfoRow label="API Port" value={systemInfo.server?.port} />
                    </Box>
                  </Box>
                </>
              )}
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 15 && (
        <Grid item xs={12}>
            <SettingsSectionBox index={15} title="Public holidays" icon={<CalendarMonthIcon color="primary" />}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Server time</strong> sets the default year ({phData.server_year ?? '—'}). Pick any{' '}
                <strong>country</strong> (worldwide ISO list), use a <strong>tab per country</strong> that has data for
                this year, then <strong>Load preset</strong> where available (USA / India), or add <strong>custom</strong>{' '}
                holidays. Dates in the table use <strong>MM/DD/YYYY</strong> for the United States and{' '}
                <strong>DD/MM/YYYY</strong> for India. <strong>Publish</strong> locks that country and year;{' '}
                <strong>Unpublish</strong> to edit again. Use <strong>Clear & reload</strong> to wipe the current country
                and year (draft only).
              </Alert>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Calendar year"
                  type="number"
                  size="small"
                  value={phYear}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setPhYear(
                      Number.isFinite(n)
                        ? Math.min(2100, Math.max(2000, n))
                        : phData.server_year || new Date().getFullYear()
                    );
                  }}
                  sx={{ width: 140 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setPhYear(phData.server_year || new Date().getFullYear())}
                  disabled={phBusy}
                >
                  Use server year
                </Button>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 280 }}
                  options={phCountryOptions}
                  getOptionLabel={(o) => `${o.name} (${o.code})`}
                  isOptionEqualToValue={(a, b) => a.code === b.code}
                  value={phCountryOptions.find((c) => c.code === phCountry) || null}
                  onChange={(_, v) => v && setPhCountry(v.code)}
                  disabled={phLoading || phBusy}
                  renderInput={(params) => <TextField {...params} label="Country (ISO)" />}
                />
                <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 200px' }}>
                  Server:{' '}
                  {phData.server_time_iso
                    ? new Date(phData.server_time_iso).toLocaleString(undefined, { hour12: true })
                    : '—'}
                </Typography>
                {phData.published ? (
                  <Chip label={`Published (${phCountry})`} color="success" />
                ) : (
                  <Chip label={`Draft (${phCountry})`} color="warning" variant="outlined" />
                )}
              </Box>

              {phTabCodes.length > 0 && (
                <Tabs
                  value={phCountry}
                  onChange={(_, v) => setPhCountry(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    mb: 2,
                    minHeight: 44,
                    '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' },
                  }}
                >
                  {phTabCodes.map((code) => {
                    const meta = phData.countries_in_year?.find((c) => c.country_code === code);
                    const published = Boolean(meta?.published);
                    const nm = Country.getCountryByCode(code)?.name || code;
                    return (
                      <Tab
                        key={code}
                        value={code}
                        label={`${nm}${published ? ' · locked' : ''}`}
                        sx={{
                          color: phCountryTabColor(code),
                          borderBottom: published ? '3px solid' : undefined,
                          borderColor: published ? 'success.main' : undefined,
                        }}
                      />
                    );
                  })}
                </Tabs>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handlePhSeedPreset}
                  disabled={phBusy || phLoading || phData.published}
                >
                  Load preset holidays
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handlePhClearCountry}
                  disabled={phBusy || phLoading || phData.published || phData.holidays.length === 0}
                >
                  Clear & reload
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    setPhCustomDlg({
                      open: true,
                      name: '',
                      officialDate: null,
                      observedDate: null,
                      observedDirty: false,
                    })
                  }
                  disabled={phBusy || phData.published}
                >
                  Add custom holiday
                </Button>
                <Button
                  color="success"
                  variant="contained"
                  onClick={handlePhPublish}
                  disabled={phBusy || phLoading || phData.published || phData.holidays.length === 0}
                >
                  Publish (lock country + year)
                </Button>
                <Button color="warning" variant="outlined" onClick={handlePhUnpublish} disabled={phBusy || !phData.published}>
                  Unpublish / revoke
                </Button>
              </Box>
              {phLoading ? (
                <CircularProgress size={28} />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Holiday</TableCell>
                        <TableCell>Official date</TableCell>
                        <TableCell>Observed date</TableCell>
                        <TableCell>Observed Y/N</TableCell>
                        <TableCell>Custom</TableCell>
                        <TableCell align="right"> </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {phData.holidays.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" color="text.secondary">
                              No rows for <strong>{phCountry}</strong> in <strong>{phYear}</strong>. Load preset (USA /
                              India) or add a custom holiday.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        phData.holidays.map((h) => (
                          <TableRow key={h.id}>
                            <TableCell>{h.name}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {h.holiday_date_display ||
                                formatPhHolidayCell(h.holiday_date, h.country_code || phCountry)}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {h.observed_date_display ||
                                formatPhHolidayCell(h.observed_date, h.country_code || phCountry)}
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={Boolean(h.use_observed)}
                                onChange={(e) => handlePhToggleObserved(h, e.target.checked)}
                                disabled={phData.published}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{h.is_custom ? 'Yes' : '—'}</TableCell>
                            <TableCell align="right">
                              {h.is_custom && !phData.published && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handlePhDeleteCustom(h)}
                                  disabled={phBusy}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 16 && (
        <Grid item xs={12}>
          <SettingsSectionBox
            index={16}
            title="Default values (organization)"
            icon={<LanguageIcon color="primary" />}
            headerActions={
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveOrgDefaults}
                disabled={savingOrgDefaults}
              >
                {savingOrgDefaults ? 'Saving…' : 'Save defaults'}
              </Button>
            }
          >
            <Alert severity="info" sx={{ mb: 2 }}>
              These drive the <strong>default country</strong> for published public holidays on PTO,{' '}
              <strong>date order</strong> (USA: MM/DD/YYYY vs India: DD/MM/YYYY), <strong>currency label</strong>, and{' '}
              <strong>organization clock</strong> (IANA timezone) on the My PTO screen. Default country is{' '}
              <strong>USA (US)</strong> when first initialized.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  size="small"
                  options={phCountryOptions}
                  getOptionLabel={(o) => `${o.name} (${o.code})`}
                  isOptionEqualToValue={(a, b) => a.code === b.code}
                  value={phCountryOptions.find((c) => c.code === orgDefaults.country) || null}
                  onChange={(_, v) => {
                    if (!v) return;
                    const code = v.code;
                    setOrgDefaults((p) => ({
                      ...p,
                      country: code,
                      timezone:
                        code === 'IN'
                          ? 'Asia/Kolkata'
                          : code === 'US'
                            ? 'America/Los_Angeles'
                            : p.timezone,
                      currency: code === 'IN' ? 'INR' : code === 'US' ? 'USD' : p.currency,
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Default country (ISO)" />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="org-default-tz">Default timezone (IANA)</InputLabel>
                  <Select
                    labelId="org-default-tz"
                    label="Default timezone (IANA)"
                    value={orgDefaults.timezone}
                    onChange={(e) => setOrgDefaults((p) => ({ ...p, timezone: e.target.value }))}
                  >
                    {(() => {
                      const cur = orgDefaults.timezone;
                      const preset = ORG_DEFAULT_TIMEZONE_OPTIONS.some((o) => o.value === cur);
                      const head = preset ? [] : [{ value: cur, label: `${cur} (current)` }];
                      return [...head, ...ORG_DEFAULT_TIMEZONE_OPTIONS].map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ));
                    })()}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Pick the zone that matches how you want “organization time” shown (e.g. Pacific for US West).
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Default currency (ISO 4217)"
                  value={orgDefaults.currency}
                  onChange={(e) =>
                    setOrgDefaults((p) => ({
                      ...p,
                      currency: String(e.target.value || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3),
                    }))
                  }
                  inputProps={{ maxLength: 3 }}
                  helperText="Examples: USD, INR, EUR"
                />
              </Grid>
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <strong>Preview:</strong> dates for <strong>{orgDefaults.country === 'IN' ? 'India' : orgDefaults.country === 'US' ? 'United States' : orgDefaults.country}</strong> use{' '}
              {orgDefaults.country === 'IN' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}; currency <strong>{orgDefaults.currency}</strong>; clocks use{' '}
              <strong>{orgDefaults.timezone}</strong>.
            </Typography>
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 17 && (
        <Grid item xs={12}>
          <SettingsSectionBox
            index={17}
            title="Demo / Test Data (UI visibility)"
            icon={<DemoUiTabIcon color="primary" />}
            headerActions={
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveDemoUiSetting}
                disabled={savingDemoUi}
              >
                {savingDemoUi ? 'Saving…' : 'Save'}
              </Button>
            }
          >
            <Alert severity="info" sx={{ mb: 2 }}>
              Turn off after QA to hide demo entry points (<strong>Demo/Test Users Actual View</strong>,{' '}
              <strong>Clear Demo/Test Data</strong>, <strong>Demo / Test Data</strong> under SEED Data) and to{' '}
              <strong>remove ABC demo mailbox users, contacts, and employees</strong> from normal lists and lookups (users,
              employees, contacts, chat directory, reports pickers, etc.). Data stays in the database until you purge it.
              Administrators can turn this back on here at any time.
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={showDemoDataInUiSetting}
                  onChange={(e) => setShowDemoDataInUiSetting(e.target.checked)}
                  color="primary"
                />
              }
              label="Show demo / test data in the application (UI + directory data)"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              After saving, refresh the page or navigate again so lists reload without demo rows.
            </Typography>
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 18 && (
        <Grid item xs={12}>
          <SettingsSectionBox
            index={18}
            title="Email Extractor"
            icon={<EmailExtractorTabIcon color="primary" />}
          >
            <EmailExtractorSettingsPanel />
          </SettingsSectionBox>
        </Grid>
        )}

        {settingsMainTab === 19 && (
        <Grid item xs={12}>
          <SettingsSectionBox
            index={19}
            title="Extract Data from Libraries"
            icon={<LibrariesTabIcon color="primary" />}
          >
            <ExtractDataLibrariesSettingsPanel />
          </SettingsSectionBox>
        </Grid>
        )}
      </Grid>

      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={String(phCountry).toUpperCase() === 'IN' ? enIN : enUS}
      >
        <Dialog
          open={phCustomDlg.open}
          onClose={() =>
            !phBusy &&
            setPhCustomDlg({ open: false, name: '', officialDate: null, observedDate: null, observedDirty: false })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add custom public holiday ({phCountry} · {phYear})</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                fullWidth
                label="Holiday name"
                value={phCustomDlg.name}
                onChange={(e) => setPhCustomDlg((d) => ({ ...d, name: e.target.value }))}
                disabled={phBusy}
              />
              <DatePicker
                label="Official date"
                value={phCustomDlg.officialDate}
                onChange={(nd) => {
                  setPhCustomDlg((d) => ({
                    ...d,
                    officialDate: nd,
                    observedDate: !d.observedDirty ? nd : d.observedDate,
                  }));
                }}
                minDate={startOfYear(new Date(phYear, 0, 1))}
                maxDate={endOfYear(new Date(phYear, 0, 1))}
                disabled={phBusy}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: `Must fall in calendar year ${phYear} (${String(phCountry).toUpperCase() === 'IN' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'})`,
                  },
                }}
              />
              <DatePicker
                label="Observed date"
                value={phCustomDlg.observedDate}
                onChange={(nd) => {
                  setPhCustomDlg((d) => ({
                    ...d,
                    observedDate: nd,
                    observedDirty: true,
                  }));
                }}
                minDate={startOfYear(new Date(phYear, 0, 1))}
                maxDate={endOfYear(new Date(phYear, 0, 1))}
                disabled={phBusy}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'Defaults to official date; change if your organization observes a different day.',
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() =>
                setPhCustomDlg({ open: false, name: '', officialDate: null, observedDate: null, observedDirty: false })
              }
              disabled={phBusy}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handlePhAddCustom} disabled={phBusy}>
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>

      <Dialog
        open={deptDialog.open}
        onClose={() => !savingOrgName && setDeptDialog((d) => ({ ...d, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{deptDialog.editing ? 'Edit department name' : 'Add department name'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Department name"
                value={deptDialog.name}
                onChange={(e) => setDeptDialog((d) => ({ ...d, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Display order"
                value={deptDialog.sort_order}
                onChange={(e) => setDeptDialog((d) => ({ ...d, sort_order: e.target.value }))}
                helperText="Lower numbers appear first"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={deptDialog.is_active}
                    onChange={(e) => setDeptDialog((d) => ({ ...d, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeptDialog((d) => ({ ...d, open: false }))} disabled={savingOrgName}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveDeptDialog}
            disabled={savingOrgName}
            startIcon={savingOrgName ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {savingOrgName ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={teamDialog.open}
        onClose={() => !savingOrgName && setTeamDialog((d) => ({ ...d, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{teamDialog.editing ? 'Edit team name' : 'Add team name'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Team name"
                value={teamDialog.name}
                onChange={(e) => setTeamDialog((d) => ({ ...d, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Display order"
                value={teamDialog.sort_order}
                onChange={(e) => setTeamDialog((d) => ({ ...d, sort_order: e.target.value }))}
                helperText="Lower numbers appear first"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={teamDialog.is_active}
                    onChange={(e) => setTeamDialog((d) => ({ ...d, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTeamDialog((d) => ({ ...d, open: false }))} disabled={savingOrgName}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveTeamDialog}
            disabled={savingOrgName}
            startIcon={savingOrgName ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {savingOrgName ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Marquee Message Dialog */}
      <Dialog
        open={marqueeDialogOpen}
        onClose={() => setMarqueeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMarquee ? 'Edit Marquee Message' : 'Add Marquee Message'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                multiline
                rows={3}
                value={marqueeForm.message}
                onChange={(e) => setMarqueeForm({ ...marqueeForm, message: e.target.value })}
                placeholder="Enter your announcement message here..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Message Type</InputLabel>
                <Select
                  value={marqueeForm.message_type}
                  label="Message Type"
                  onChange={(e) => setMarqueeForm({ ...marqueeForm, message_type: e.target.value })}
                >
                  <MenuItem value="info">Info (Blue)</MenuItem>
                  <MenuItem value="success">Success (Green)</MenuItem>
                  <MenuItem value="warning">Warning (Yellow) - Flashing</MenuItem>
                  <MenuItem value="error">Error (Red) - Flashing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Priority"
                type="number"
                value={marqueeForm.priority}
                onChange={(e) => setMarqueeForm({ ...marqueeForm, priority: parseInt(e.target.value) || 0 })}
                helperText="Higher priority = appears first"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date (Optional)"
                type="date"
                value={marqueeForm.start_date}
                onChange={(e) => setMarqueeForm({ ...marqueeForm, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date (Optional)"
                type="date"
                value={marqueeForm.end_date}
                onChange={(e) => setMarqueeForm({ ...marqueeForm, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={marqueeForm.is_active}
                    onChange={(e) => setMarqueeForm({ ...marqueeForm, is_active: e.target.checked })}
                  />
                }
                label="Active (Message will be displayed)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setMarqueeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveMarquee}
            disabled={savingMarquee}
            startIcon={savingMarquee ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {savingMarquee ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Idle Settings Dialog */}
      <Dialog
        open={userIdleDialogOpen}
        onClose={() => setUserIdleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Idle Timeout - {editingUserIdle?.firstName} {editingUserIdle?.lastName}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Leave empty to use system default ({usersIdleSettings.systemDefault} minutes)
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Idle Timeout (minutes)"
                type="number"
                value={userIdleForm.idle_timeout_minutes}
                onChange={(e) => setUserIdleForm({ ...userIdleForm, idle_timeout_minutes: e.target.value })}
                placeholder={`Default: ${usersIdleSettings.systemDefault} minutes`}
                InputProps={{
                  endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                }}
                helperText="Custom idle timeout for this user (1-60 minutes)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Custom Setting"
                multiline
                rows={2}
                value={userIdleForm.idle_timeout_reason}
                onChange={(e) => setUserIdleForm({ ...userIdleForm, idle_timeout_reason: e.target.value })}
                placeholder="e.g., User has slow internet connection, needs more time between activities"
                helperText="Document why this user has a different idle timeout"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setUserIdleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveUserIdle}
            disabled={savingUserIdle}
            startIcon={savingUserIdle ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {savingUserIdle ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper component for info rows
const InfoRow = ({ label, value, code = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    {code ? (
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          bgcolor: 'action.hover',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          fontSize: '0.75rem',
          maxWidth: '60%',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {value || 'N/A'}
      </Typography>
    ) : (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || 'N/A'}
      </Typography>
    )}
  </Box>
);

export default Settings;
